import { resolveMenuItemTitle } from '@ordo/constants/menu-item-title'
import { Button } from '@ordo/ui/button'
import { Skeleton } from '@ordo/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { Utensils } from 'lucide-react'
import type { ReactElement } from 'react'

import { fetchLocationActiveMenus } from '@/api/orders'
import { serviceDateIso } from '@/api/school-orders'
import { type Mealtime, MEALTIME_LABELS, sortMealtimes } from '@/lib/mealtime'
import { dateFromKey } from '@/lib/school-orders/calendar'

/**
 * The school's field-trip meals for the date. Separate query key and no
 * previous-date placeholder: never show yesterday's meal under today's date.
 * The dialog reads the same query for its per-meal quantity inputs.
 */
export function useFieldTripMenuPreview({
  locationId,
  serviceDate,
  mealtimes,
  enabled,
}: {
  locationId: string
  serviceDate: string
  mealtimes: readonly Mealtime[]
  enabled: boolean
}) {
  return useQuery({
    queryKey: ['field-trip-menu-preview', locationId, serviceDate],
    queryFn: ({ signal }) =>
      fetchLocationActiveMenus(
        { locationId, activeOn: serviceDateIso(serviceDate), fieldTripPreview: true, withAllergens: true },
        signal,
      ),
    enabled: enabled && Boolean(serviceDate) && mealtimes.length > 0,
    staleTime: 30_000,
  })
}

export function FieldTripMenuPreview({
  locationId,
  serviceDate,
  mealtimes,
  enabled,
}: {
  locationId: string
  serviceDate: string
  mealtimes: Mealtime[]
  enabled: boolean
}): ReactElement {
  const query = useFieldTripMenuPreview({ locationId, serviceDate, mealtimes, enabled })
  const dayLabel = serviceDate
    ? dateFromKey(serviceDate).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : ''
  return (
    <section
      className="shrink-0 overflow-hidden rounded-lg border"
      aria-label="Available field-trip meals"
      aria-live="polite">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-muted/30 px-4 py-3">
        <span className="flex items-center gap-2 text-sm font-medium">
          <Utensils className="h-4 w-4" aria-hidden="true" />
          On the menu
        </span>
        <span className="text-xs text-muted-foreground">{dayLabel}</span>
      </div>
      {!serviceDate ? (
        <p className="p-4 text-sm text-muted-foreground">Select a date to preview your field-trip meals.</p>
      ) : mealtimes.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">Select a meal to see what's available.</p>
      ) : query.isPending ? (
        <div className="space-y-2 p-4" role="status" aria-label="Loading field-trip menu">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-4 w-56 max-w-full" />
          <Skeleton className="h-3 w-40" />
        </div>
      ) : query.isError ? (
        <div className="space-y-2 p-4">
          <p className="text-sm text-muted-foreground">We couldn't load the menu preview.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        sortMealtimes(mealtimes).map((mealtime) => {
          const menus = (query.data?.menusForLocation ?? []).filter((menu) => menu.mealtime === mealtime)
          return (
            <div key={mealtime} className="border-t p-4 first:border-t-0">
              <p className="mb-1 text-xs text-muted-foreground">{MEALTIME_LABELS[mealtime]} · Bagged to go</p>
              {menus.length === 0 ? (
                <p className="text-sm text-muted-foreground">No field-trip meals available for this date.</p>
              ) : (
                menus.map((menu) => (
                  <div key={menu.id} className="space-y-3">
                    {menu.gradeRangeCodes.length > 0 ? (
                      <p className="text-xs text-muted-foreground">{menu.gradeRangeCodes.join(', ')}</p>
                    ) : null}
                    {menu.items.map((item) => (
                      <div key={item.id}>
                        <p className="text-sm font-medium">{resolveMenuItemTitle(item) ?? 'Field-trip meal'}</p>
                        {item.servedComponents?.length ? (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {item.servedComponents.map((part) => part.name).join(' · ')}
                          </p>
                        ) : null}
                        <details className="mt-2 text-xs">
                          <summary className="cursor-pointer">Allergens</summary>
                          <p className="mt-1 text-muted-foreground">
                            {item.allergenCoverage !== 'EVIDENCED'
                              ? 'Allergen information is not available.'
                              : item.allergens?.length
                                ? `Contains ${item.allergens.map((allergen) => allergen.name).join(', ')}`
                                : 'No reviewed allergens listed.'}
                          </p>
                        </details>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          )
        })
      )}
    </section>
  )
}
